/**
 * F-JP-30 — the full structural contract for a journal record.
 *
 * There is one implementation for two jobs, and that is the point. The spec
 * says the contract is "enforced on both write and decode": a record this build
 * would refuse to write must also be a record it refuses to *load*, or a file
 * damaged in place would quietly become the value the app shows. Writing the
 * validator as a decoder over `unknown` and then feeding a typed value through
 * the same function is the only way to keep those two answers identical — two
 * validators drift the first time a field is added to one of them.
 *
 * Three consequences worth stating, because each one looks like over-strictness
 * until the failure it prevents is named:
 *
 *  1. **The decoder rebuilds the value rather than casting the input.** What
 *     comes back is made only of fields this build understands, so the
 *     canonical bytes of the decoded record are comparable with the bytes on
 *     disk — which is exactly the comparison F-JP-20's post-write read-back
 *     performs. A cast would make that comparison vacuous.
 *  2. **Unknown properties are rejected, not ignored.** Within a schema version
 *     an unrecognised field is damage, and forward compatibility is the schema
 *     version's job (`futureSchema`), not the field reader's. Ignoring it would
 *     silently drop data on the next save.
 *  3. **Nothing is repaired.** Not a nesting level, not a mark order, not a tag
 *     list. A malformed persisted record reaches the quarantine lane with its
 *     bytes intact (F-JE-01); repairing it here would overwrite the evidence
 *     with a guess.
 *
 * The one place write and decode legitimately differ is `tags`. The store
 * derives its tag index by folding, deduplicating and sorting, but the payload
 * is preserved byte-exact on decode (`02-ARCHITECTURE.md` §4). So canonical
 * tag order is required of a *caller* and tolerated in a *file*.
 */

import type {
  JournalAttachment,
  JournalAttachmentId,
  JournalBlock,
  JournalDocument,
  JournalDraft,
  JournalEffortLink,
  JournalEffortSnapshot,
  JournalEmbed,
  JournalEntry,
  JournalEntryKind,
  JournalFeeling,
  JournalGoal,
  JournalGoalLink,
  JournalMigrationMarker,
  JournalPromptFamily,
  JournalPromptReference,
  JournalTextBlock,
  JournalTextMark,
  JournalTextRun,
  JournalWorkoutReflection,
} from "@ff/domain";
import {
  JOURNAL_ATTACHMENT_SCHEMA_VERSION,
  JOURNAL_BLOCK_STYLES,
  JOURNAL_DOCUMENT_SCHEMA_VERSION,
  JOURNAL_DRAFT_SCHEMA_VERSION,
  JOURNAL_ENTRY_SCHEMA_VERSION,
  JOURNAL_GOAL_SCHEMA_VERSION,
  JOURNAL_MAX_NESTING_LEVEL,
  JOURNAL_PROMPT_FAMILY_TITLES,
  JOURNAL_REVISION_MAX,
  areJournalMarksCanonical,
  isJournalInstant,
  isJournalListStyle,
  isSha256Hex,
  normalizeJournalTags,
  planJournalDocumentMigration,
} from "@ff/domain";

/* ------------------------------------------------------------------------- *
 * Result
 * ------------------------------------------------------------------------- */

export interface JournalValidationIssue {
  /** `$.attachments[0].contentSHA256` — the field, never just "invalid". */
  readonly path: string;
  readonly detail: string;
}

export type JournalValidation<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly issue: JournalValidationIssue }
  /**
   * A nested schema version this build is too old to read. It is separated from
   * an ordinary issue because the recovery row must say `futureSchema(found,
   * supported)` rather than "invalid content" — the difference between "update
   * the app" and "this file is damaged".
   */
  | {
      readonly ok: false;
      readonly issue: JournalValidationIssue;
      readonly futureSchema: { readonly found: number; readonly supported: number };
    };

/**
 * Where the record came from.
 *
 * `write` is the stricter of the two: it additionally requires canonical tag
 * ordering, which a file is allowed to lack because tags are preserved
 * byte-exact on decode.
 */
export type JournalValidationMode = "write" | "decode";

/* ------------------------------------------------------------------------- *
 * Internals: a fail-fast reader
 * ------------------------------------------------------------------------- */

class Invalid extends Error {
  readonly issue: JournalValidationIssue;
  readonly futureSchema?: { readonly found: number; readonly supported: number };

  constructor(
    path: string,
    detail: string,
    futureSchema?: { readonly found: number; readonly supported: number },
  ) {
    super(`${detail} at ${path}`);
    this.name = "JournalInvalidRecord";
    this.issue = { path, detail };
    this.futureSchema = futureSchema;
  }
}

function invalid(path: string, detail: string): never {
  throw new Invalid(path, detail);
}

function run<T>(build: () => T): JournalValidation<T> {
  try {
    return { ok: true, value: build() };
  } catch (error) {
    if (error instanceof Invalid) {
      return error.futureSchema === undefined
        ? { ok: false, issue: error.issue }
        : { ok: false, issue: error.issue, futureSchema: error.futureSchema };
    }
    throw error;
  }
}

function object(raw: unknown, path: string, allowed: readonly string[]): Record<string, unknown> {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    invalid(path, "expected an object");
  }
  const value = raw as Record<string, unknown>;
  for (const key of Object.keys(value)) {
    // `undefined` and absent are the same thing once encoded canonically, so a
    // present-but-undefined optional property is not an unknown field.
    if (!allowed.includes(key) && value[key] !== undefined) {
      invalid(`${path}.${key}`, "unrecognised field");
    }
  }
  return value;
}

function array(raw: unknown, path: string): readonly unknown[] {
  if (!Array.isArray(raw)) invalid(path, "expected an array");
  return raw;
}

function text(raw: unknown, path: string): string {
  if (typeof raw !== "string") invalid(path, "expected a string");
  return raw;
}

/** A string that is non-empty and carries no leading or trailing whitespace. */
function trimmedText(raw: unknown, path: string): string {
  const value = text(raw, path);
  if (value.length === 0) invalid(path, "must not be empty");
  if (value !== value.trim()) invalid(path, "must be trimmed");
  return value;
}

function optionalTrimmedText(raw: unknown, path: string): string | undefined {
  return raw === undefined ? undefined : trimmedText(raw, path);
}

function integer(raw: unknown, path: string): number {
  if (typeof raw !== "number" || !Number.isFinite(raw)) {
    invalid(path, "expected a finite number");
  }
  if (!Number.isSafeInteger(raw)) invalid(path, "expected a safe integer");
  return raw;
}

function boolean(raw: unknown, path: string): boolean {
  if (typeof raw !== "boolean") invalid(path, "expected a boolean");
  return raw;
}

/** A finite instant inside the canonical epoch-microsecond range (F-JE-02). */
function instant(raw: unknown, path: string): number {
  const value = integer(raw, path);
  if (!isJournalInstant(value)) invalid(path, "outside the canonical instant range");
  return value;
}

function duration(raw: unknown, path: string): number {
  const value = integer(raw, path);
  if (value < 0) invalid(path, "must not be negative");
  return value;
}

function literalNumber(raw: unknown, path: string, expected: number): number {
  const value = integer(raw, path);
  if (value !== expected) invalid(path, `expected ${expected}, found ${value}`);
  return value;
}

/**
 * A record-level schema version. Above the supported version is a *future*
 * schema, which is preserved rather than blamed; below it is damage, because
 * every historical version this build can still read has a migration and there
 * are none registered below the current one.
 */
function schemaVersionAtMost(raw: unknown, path: string, supported: number): number {
  const found = integer(raw, path);
  if (found > supported) {
    throw new Invalid(path, `record is version ${found}`, { found, supported });
  }
  if (found !== supported) invalid(path, `unsupported version ${found}`);
  return found;
}

function member<T extends string>(
  raw: unknown,
  path: string,
  allowed: readonly T[],
): T {
  const value = text(raw, path);
  const found = allowed.find((candidate) => candidate === value);
  if (found === undefined) invalid(path, `unrecognised value ${JSON.stringify(value)}`);
  return found;
}

/* ------------------------------------------------------------------------- *
 * Leaf shapes
 * ------------------------------------------------------------------------- */

const MARK_KINDS = ["strong", "emphasis", "underline", "strikethrough", "link"] as const;

function readMark(raw: unknown, path: string): JournalTextMark {
  const source = object(raw, path, ["kind", "value"]);
  const kind = member(source["kind"], `${path}.kind`, MARK_KINDS);
  if (kind === "link") {
    // A link value is required exactly when the kind is `link`; the "underline
    // with a href" state has no representation and must not be storable.
    const value = text(source["value"], `${path}.value`);
    if (value.length === 0) invalid(`${path}.value`, "a link must carry a destination");
    return { kind, value };
  }
  if (source["value"] !== undefined) {
    invalid(`${path}.value`, `a ${kind} mark must not carry a value`);
  }
  return { kind };
}

function readRun(raw: unknown, path: string): JournalTextRun {
  const source = object(raw, path, ["text", "marks"]);
  const value = text(source["text"], `${path}.text`);
  const marks = array(source["marks"], `${path}.marks`).map((mark, index) =>
    readMark(mark, `${path}.marks[${index}]`),
  );
  if (!areJournalMarksCanonical(marks)) {
    // Canonical order is what makes two structurally identical runs encode to
    // the same bytes, and this package content-hashes records.
    invalid(`${path}.marks`, "marks are not in canonical order");
  }
  return { text: value, marks };
}

function readTextBlock(
  source: Record<string, unknown>,
  path: string,
  id: string,
): JournalTextBlock {
  const style = member(source["style"], `${path}.style`, JOURNAL_BLOCK_STYLES);
  const nestingLevel = integer(source["nestingLevel"], `${path}.nestingLevel`);
  const runs = array(source["runs"], `${path}.runs`).map((run_, index) =>
    readRun(run_, `${path}.runs[${index}]`),
  );

  if (!isJournalListStyle(style)) {
    // Only list styles nest. A nested quote or heading has no rendering, so a
    // stored one is a value no build could have produced.
    if (nestingLevel !== 0) {
      invalid(`${path}.nestingLevel`, `${style} must nest at level 0`);
    }
    if (source["isChecked"] !== undefined) {
      invalid(`${path}.isChecked`, `${style} must not carry isChecked`);
    }
    return { kind: "text", id: id as JournalTextBlock["id"], style, nestingLevel: 0, runs };
  }

  if (nestingLevel < 0 || nestingLevel > JOURNAL_MAX_NESTING_LEVEL) {
    invalid(`${path}.nestingLevel`, `must be 0…${JOURNAL_MAX_NESTING_LEVEL}`);
  }
  const level = nestingLevel as 0 | 1 | 2 | 3 | 4 | 5 | 6;

  if (style === "checklistItem") {
    return {
      kind: "text",
      id: id as JournalTextBlock["id"],
      style,
      nestingLevel: level,
      isChecked: boolean(source["isChecked"], `${path}.isChecked`),
      runs,
    };
  }
  if (source["isChecked"] !== undefined) {
    invalid(`${path}.isChecked`, `${style} must not carry isChecked`);
  }
  return { kind: "text", id: id as JournalTextBlock["id"], style, nestingLevel: level, runs };
}

function readEffortSnapshot(raw: unknown, path: string): JournalEffortSnapshot {
  const source = object(raw, path, [
    "activityName",
    "source",
    "startedAt",
    "endedAt",
    "activeDurationMilliseconds",
    "wallDurationMilliseconds",
    "outcome",
    "intervalSummary",
  ]);
  const startedAt = instant(source["startedAt"], `${path}.startedAt`);
  const endedAt = instant(source["endedAt"], `${path}.endedAt`);
  if (endedAt < startedAt) {
    invalid(`${path}.endedAt`, "must not be earlier than startedAt");
  }
  const active = duration(
    source["activeDurationMilliseconds"],
    `${path}.activeDurationMilliseconds`,
  );
  const wall = duration(
    source["wallDurationMilliseconds"],
    `${path}.wallDurationMilliseconds`,
  );
  if (wall < active) {
    // Wall time contains active time by construction; the reverse describes a
    // session that was working while the clock was not running.
    invalid(`${path}.wallDurationMilliseconds`, "must not be less than active duration");
  }

  const snapshot: JournalEffortSnapshot = {
    activityName: trimmedText(source["activityName"], `${path}.activityName`),
    source: trimmedText(source["source"], `${path}.source`),
    startedAt: startedAt as JournalEffortSnapshot["startedAt"],
    endedAt: endedAt as JournalEffortSnapshot["endedAt"],
    activeDurationMilliseconds: active,
    wallDurationMilliseconds: wall,
    outcome: trimmedText(source["outcome"], `${path}.outcome`),
  };
  const intervalSummary = optionalTrimmedText(
    source["intervalSummary"],
    `${path}.intervalSummary`,
  );
  return intervalSummary === undefined ? snapshot : { ...snapshot, intervalSummary };
}

function readEffortLink(raw: unknown, path: string): JournalEffortLink {
  const source = object(raw, path, ["sessionID", "snapshot"]);
  return {
    sessionID: trimmedText(source["sessionID"], `${path}.sessionID`) as
      JournalEffortLink["sessionID"],
    snapshot: readEffortSnapshot(source["snapshot"], `${path}.snapshot`),
  };
}

const PROMPT_FAMILIES = Object.keys(JOURNAL_PROMPT_FAMILY_TITLES) as JournalPromptFamily[];

function readPrompt(raw: unknown, path: string): JournalPromptReference {
  const source = object(raw, path, ["id", "family", "text", "catalogRevision"]);
  const reference: JournalPromptReference = {
    id: trimmedText(source["id"], `${path}.id`) as JournalPromptReference["id"],
    text: trimmedText(source["text"], `${path}.text`),
  };
  const family =
    source["family"] === undefined
      ? undefined
      : member(source["family"], `${path}.family`, PROMPT_FAMILIES);
  const catalogRevision =
    source["catalogRevision"] === undefined
      ? undefined
      : integer(source["catalogRevision"], `${path}.catalogRevision`);
  if (catalogRevision !== undefined && catalogRevision < 0) {
    invalid(`${path}.catalogRevision`, "must not be negative");
  }
  return {
    ...reference,
    ...(family === undefined ? {} : { family }),
    ...(catalogRevision === undefined ? {} : { catalogRevision }),
  };
}

function readGoalLink(raw: unknown, path: string): JournalGoalLink {
  const source = object(raw, path, ["goalID", "titleSnapshot"]);
  return {
    goalID: trimmedText(source["goalID"], `${path}.goalID`) as JournalGoalLink["goalID"],
    // The snapshot is what keeps a past entry readable after the goal is
    // deleted, so an empty one defeats the reason the field exists.
    titleSnapshot: trimmedText(source["titleSnapshot"], `${path}.titleSnapshot`),
  };
}

function readEmbed(raw: unknown, path: string): JournalEmbed {
  const source = object(raw, path, ["kind", "effort", "media", "prompt"]);
  const kind = member(source["kind"], `${path}.kind`, ["effort", "media", "prompt"] as const);
  switch (kind) {
    case "effort":
      return { kind, effort: readEffortLink(source["effort"], `${path}.effort`) };
    case "prompt":
      return { kind, prompt: readPrompt(source["prompt"], `${path}.prompt`) };
    case "media": {
      const media = object(source["media"], `${path}.media`, ["attachmentId"]);
      const attachmentId = trimmedText(
        media["attachmentId"],
        `${path}.media.attachmentId`,
      ) as JournalAttachmentId;
      return { kind, media: { attachmentId } };
    }
  }
}

function readBlock(raw: unknown, path: string): JournalBlock {
  const source = object(raw, path, [
    "kind",
    "id",
    "style",
    "nestingLevel",
    "isChecked",
    "runs",
    "embed",
  ]);
  const kind = member(source["kind"], `${path}.kind`, ["text", "embed"] as const);
  const id = trimmedText(source["id"], `${path}.id`);
  if (kind === "embed") {
    for (const forbidden of ["style", "nestingLevel", "isChecked", "runs"]) {
      if (source[forbidden] !== undefined) {
        invalid(`${path}.${forbidden}`, "an embed block carries no text fields");
      }
    }
    return { kind, id: id as JournalBlock["id"], embed: readEmbed(source["embed"], `${path}.embed`) };
  }
  if (source["embed"] !== undefined) {
    invalid(`${path}.embed`, "a text block carries no embed");
  }
  return readTextBlock(source, path, id);
}

function readDocument(raw: unknown, path: string): JournalDocument {
  const source = object(raw, path, ["schemaVersion", "blocks"]);
  const found = integer(source["schemaVersion"], `${path}.schemaVersion`);
  const plan = planJournalDocumentMigration(found);
  if (plan.kind === "futureSchema") {
    // The registry, not a comparison here, is the authority on what this build
    // can read — so a later document version becomes readable by registering a
    // step rather than by editing this check.
    throw new Invalid(`${path}.schemaVersion`, `document is version ${found}`, {
      found: plan.found,
      supported: plan.supported,
    });
  }
  if (plan.kind !== "current") {
    invalid(`${path}.schemaVersion`, `unsupported document version ${found}`);
  }

  const blocks = array(source["blocks"], `${path}.blocks`).map((block, index) =>
    readBlock(block, `${path}.blocks[${index}]`),
  );

  const seen = new Set<string>();
  for (const [index, block] of blocks.entries()) {
    if (seen.has(block.id)) {
      // Block ids address selections, undo entries and accessibility elements;
      // two blocks under one id make every one of those ambiguous.
      invalid(`${path}.blocks[${index}].id`, "duplicate block id");
    }
    seen.add(block.id);
  }

  return { schemaVersion: JOURNAL_DOCUMENT_SCHEMA_VERSION, blocks };
}

const ATTACHMENT_PURPOSES = [
  "general",
  "progressPhoto",
  "performanceVideo",
  "formReference",
  "voiceRecap",
] as const;

function readAttachment(raw: unknown, path: string): JournalAttachment {
  const source = object(raw, path, [
    "schemaVersion",
    "id",
    "ownerEntryID",
    "contentType",
    "byteCount",
    "contentSHA256",
    "caption",
    "accessibilityDescription",
    "createdAt",
    "purpose",
    "mediaType",
    "isConcealed",
    "durationMilliseconds",
  ]);
  literalNumber(
    source["schemaVersion"],
    `${path}.schemaVersion`,
    JOURNAL_ATTACHMENT_SCHEMA_VERSION,
  );

  const digest = text(source["contentSHA256"], `${path}.contentSHA256`);
  if (!isSha256Hex(digest)) {
    invalid(`${path}.contentSHA256`, "expected 64 lowercase hex characters");
  }
  const byteCount = duration(source["byteCount"], `${path}.byteCount`);

  const shape = {
    schemaVersion: JOURNAL_ATTACHMENT_SCHEMA_VERSION,
    id: trimmedText(source["id"], `${path}.id`) as JournalAttachment["id"],
    ownerEntryID: trimmedText(source["ownerEntryID"], `${path}.ownerEntryID`) as
      JournalAttachment["ownerEntryID"],
    contentType: trimmedText(source["contentType"], `${path}.contentType`),
    byteCount,
    contentSHA256: digest,
    createdAt: instant(source["createdAt"], `${path}.createdAt`) as
      JournalAttachment["createdAt"],
  } as const;

  const caption = optionalTrimmedText(source["caption"], `${path}.caption`);
  const description = optionalTrimmedText(
    source["accessibilityDescription"],
    `${path}.accessibilityDescription`,
  );
  const optional = {
    ...(caption === undefined ? {} : { caption }),
    ...(description === undefined ? {} : { accessibilityDescription: description }),
  };

  const purpose = member(source["purpose"], `${path}.purpose`, ATTACHMENT_PURPOSES);
  const mediaType = member(source["mediaType"], `${path}.mediaType`, [
    "photo",
    "video",
    "audio",
  ] as const);
  const isConcealed = boolean(source["isConcealed"], `${path}.isConcealed`);
  const durationRaw = source["durationMilliseconds"];

  function rejectDuration(): void {
    if (durationRaw !== undefined) {
      invalid(`${path}.durationMilliseconds`, `a ${purpose} attachment carries no duration`);
    }
  }

  switch (purpose) {
    case "progressPhoto":
      if (mediaType !== "photo") invalid(`${path}.mediaType`, "a progress photo is a photo");
      if (!isConcealed) {
        // Concealment is a property of the purpose, not a preference, so a
        // visible progress photo is a state no build could have written.
        invalid(`${path}.isConcealed`, "a progress photo is always concealed");
      }
      rejectDuration();
      return { ...shape, ...optional, purpose, mediaType: "photo", isConcealed: true };
    case "performanceVideo":
      if (mediaType !== "video") invalid(`${path}.mediaType`, "a performance video is a video");
      rejectDuration();
      return { ...shape, ...optional, purpose, mediaType: "video", isConcealed };
    case "voiceRecap": {
      if (mediaType !== "audio") invalid(`${path}.mediaType`, "a voice recap is audio");
      const length = integer(durationRaw, `${path}.durationMilliseconds`);
      if (length <= 0) {
        invalid(`${path}.durationMilliseconds`, "must be a positive whole millisecond count");
      }
      return {
        ...shape,
        ...optional,
        purpose,
        mediaType: "audio",
        isConcealed,
        durationMilliseconds: length,
      };
    }
    case "general":
    case "formReference":
      if (mediaType === "audio") {
        // Audio is only ever a voice recap; the union has no other audio arm.
        invalid(`${path}.mediaType`, `a ${purpose} attachment is a photo or a video`);
      }
      rejectDuration();
      return { ...shape, ...optional, purpose, mediaType, isConcealed };
  }
}

function readFeeling(raw: unknown, path: string): JournalFeeling {
  const source = object(raw, path, ["label", "valence"]);
  const label = trimmedText(source["label"], `${path}.label`);
  if (source["valence"] === undefined) return { label };
  const valence = integer(source["valence"], `${path}.valence`);
  if (valence < -5 || valence > 5) invalid(`${path}.valence`, "must be −5…5");
  return { label, valence: valence as JournalFeeling["valence"] };
}

function readReflection(raw: unknown, path: string): JournalWorkoutReflection {
  const source = object(raw, path, ["perceivedEffort", "bodyObservation"]);
  const effort = integer(source["perceivedEffort"], `${path}.perceivedEffort`);
  if (effort < 1 || effort > 10) invalid(`${path}.perceivedEffort`, "must be 1…10");
  return {
    perceivedEffort: effort as JournalWorkoutReflection["perceivedEffort"],
    bodyObservation: trimmedText(source["bodyObservation"], `${path}.bodyObservation`),
  };
}

function readMigration(raw: unknown, path: string, entryId: string): JournalMigrationMarker {
  const source = object(raw, path, [
    "source",
    "sourceID",
    "sourceSchemaVersion",
    "sourceContentSHA256",
  ]);
  const sourceID = trimmedText(source["sourceID"], `${path}.sourceID`);
  if (sourceID !== entryId) {
    // The marker is what makes the legacy migration idempotent. A marker
    // pointing at a different record would let the same legacy note migrate
    // twice, under two ids.
    invalid(`${path}.sourceID`, "must equal the entry's own id");
  }
  const digest = text(source["sourceContentSHA256"], `${path}.sourceContentSHA256`);
  if (!isSha256Hex(digest)) {
    invalid(`${path}.sourceContentSHA256`, "expected 64 lowercase hex characters");
  }
  const version = integer(source["sourceSchemaVersion"], `${path}.sourceSchemaVersion`);
  if (version < 0) invalid(`${path}.sourceSchemaVersion`, "must not be negative");
  return {
    source: member(source["source"], `${path}.source`, ["legacyCornerNote"] as const),
    sourceID: sourceID as JournalMigrationMarker["sourceID"],
    sourceSchemaVersion: version,
    sourceContentSHA256: digest,
  };
}

/* ------------------------------------------------------------------------- *
 * Entry
 * ------------------------------------------------------------------------- */

const ENTRY_KINDS = [
  "general",
  "workoutRecap",
  "goalReflection",
  "personal",
] as const satisfies readonly JournalEntryKind[];

const ENTRY_FIELDS = [
  "schemaVersion",
  "id",
  "revision",
  "occurredAt",
  "createdAt",
  "updatedAt",
  "title",
  "document",
  "kind",
  "feeling",
  "energy",
  "isFavorite",
  "tags",
  "prompt",
  "goal",
  "attachments",
  "migration",
  "effort",
  "workoutReflection",
];

export function decodeJournalEntry(
  raw: unknown,
  mode: JournalValidationMode = "decode",
): JournalValidation<JournalEntry> {
  return run(() => buildEntry(raw, "$", mode));
}

function buildEntry(raw: unknown, path: string, mode: JournalValidationMode): JournalEntry {
  const source = object(raw, path, ENTRY_FIELDS);
  schemaVersionAtMost(
    source["schemaVersion"],
    `${path}.schemaVersion`,
    JOURNAL_ENTRY_SCHEMA_VERSION,
  );

  const id = trimmedText(source["id"], `${path}.id`);
  const revision = integer(source["revision"], `${path}.revision`);
  if (revision < 1) invalid(`${path}.revision`, "must be at least 1");
  if (revision > JOURNAL_REVISION_MAX) invalid(`${path}.revision`, "beyond the revision ceiling");

  const createdAt = instant(source["createdAt"], `${path}.createdAt`);
  const updatedAt = instant(source["updatedAt"], `${path}.updatedAt`);
  if (updatedAt < createdAt) {
    invalid(`${path}.updatedAt`, "must not be earlier than createdAt");
  }

  const tags = array(source["tags"], `${path}.tags`).map((tag, index) =>
    trimmedText(tag, `${path}.tags[${index}]`),
  );
  if (mode === "write") {
    const canonical = normalizeJournalTags(tags);
    if (
      canonical.length !== tags.length ||
      canonical.some((tag, index) => tag !== tags[index])
    ) {
      // Only a caller is held to this. A file's tags are preserved byte-exact
      // on decode, because the tag *index* is derived at write time and never
      // written back into the payload (`02-ARCHITECTURE.md` §4).
      invalid(`${path}.tags`, "tags are not folded, deduplicated and sorted");
    }
  }

  const attachments = array(source["attachments"], `${path}.attachments`).map(
    (attachment, index) => readAttachment(attachment, `${path}.attachments[${index}]`),
  );
  for (let index = 1; index < attachments.length; index += 1) {
    const previous = attachments[index - 1];
    const current = attachments[index];
    if (previous === undefined || current === undefined) continue;
    if (previous.id === current.id) invalid(`${path}.attachments[${index}].id`, "duplicate id");
    if (previous.id > current.id) {
      // Sorted by id everywhere, so the media embed order inside the document
      // is derivable from this list rather than being a second ordering that
      // could disagree with it.
      invalid(`${path}.attachments[${index}].id`, "attachments must be sorted by id");
    }
  }

  const document = readDocument(source["document"], `${path}.document`);
  const effort =
    source["effort"] === undefined
      ? undefined
      : readEffortLink(source["effort"], `${path}.effort`);
  const prompt =
    source["prompt"] === undefined
      ? undefined
      : readPrompt(source["prompt"], `${path}.prompt`);

  checkEmbeds(document, path, effort, prompt, attachments);

  const reflectionRaw = source["workoutReflection"];
  if (reflectionRaw !== undefined && effort === undefined) {
    // A perceived-effort score with no workout to score is uninterpretable.
    invalid(`${path}.workoutReflection`, "requires an effort link");
  }
  const workoutReflection =
    reflectionRaw === undefined
      ? undefined
      : readReflection(reflectionRaw, `${path}.workoutReflection`);

  const energyRaw = source["energy"];
  let energy: 1 | 2 | 3 | 4 | 5 | undefined;
  if (energyRaw !== undefined) {
    const level = integer(energyRaw, `${path}.energy`);
    if (level < 1 || level > 5) invalid(`${path}.energy`, "must be 1…5");
    energy = level as 1 | 2 | 3 | 4 | 5;
  }

  const shape = {
    schemaVersion: JOURNAL_ENTRY_SCHEMA_VERSION,
    id: id as JournalEntry["id"],
    revision: revision as JournalEntry["revision"],
    occurredAt: instant(source["occurredAt"], `${path}.occurredAt`) as
      JournalEntry["occurredAt"],
    createdAt: createdAt as JournalEntry["createdAt"],
    updatedAt: updatedAt as JournalEntry["updatedAt"],
    document,
    isFavorite: boolean(source["isFavorite"], `${path}.isFavorite`),
    tags,
    attachments,
  } as const;

  const optional = {
    ...optionalField("title", optionalTrimmedText(source["title"], `${path}.title`)),
    ...optionalField(
      "kind",
      source["kind"] === undefined
        ? undefined
        : member(source["kind"], `${path}.kind`, ENTRY_KINDS),
    ),
    ...optionalField(
      "feeling",
      source["feeling"] === undefined
        ? undefined
        : readFeeling(source["feeling"], `${path}.feeling`),
    ),
    ...optionalField("energy", energy),
    ...optionalField("prompt", prompt),
    ...optionalField(
      "goal",
      source["goal"] === undefined
        ? undefined
        : readGoalLink(source["goal"], `${path}.goal`),
    ),
    ...optionalField(
      "migration",
      source["migration"] === undefined
        ? undefined
        : readMigration(source["migration"], `${path}.migration`, id),
    ),
  };

  if (effort === undefined) return { ...shape, ...optional };
  return {
    ...shape,
    ...optional,
    effort,
    ...optionalField("workoutReflection", workoutReflection),
  };
}

function optionalField<K extends string, V>(
  key: K,
  value: V | undefined,
): Record<K, V> | Record<string, never> {
  return value === undefined ? {} : ({ [key]: value } as Record<K, V>);
}

/**
 * The document's embeds and the entry's own context must describe the same
 * facts (F-JP-30).
 *
 * These are the checks that keep the rendered entry and the stored entry from
 * disagreeing: an effort embed the entry has no link for renders a workout that
 * the entry says never happened, and a media embed with no attachment renders a
 * frame with nothing behind it.
 */
function checkEmbeds(
  document: JournalDocument,
  entryPath: string,
  effort: JournalEffortLink | undefined,
  prompt: JournalPromptReference | undefined,
  attachments: readonly JournalAttachment[],
): void {
  const path = `${entryPath}.document`;
  const effortEmbeds = document.blocks.filter(
    (block) => block.kind === "embed" && block.embed.kind === "effort",
  );
  const promptEmbeds = document.blocks.filter(
    (block) => block.kind === "embed" && block.embed.kind === "prompt",
  );

  if (effort === undefined) {
    if (effortEmbeds.length > 0) {
      invalid(`${path}.blocks`, "an effort embed with no effort link");
    }
  } else if (effortEmbeds.length !== 1) {
    invalid(`${path}.blocks`, `expected exactly one effort embed, found ${effortEmbeds.length}`);
  }

  if (prompt === undefined) {
    if (promptEmbeds.length > 0) {
      invalid(`${path}.blocks`, "a prompt embed with no prompt reference");
    }
  } else if (promptEmbeds.length !== 1) {
    invalid(`${path}.blocks`, `expected exactly one prompt embed, found ${promptEmbeds.length}`);
  }

  const embedded: string[] = [];
  for (const block of document.blocks) {
    if (block.kind !== "embed" || block.embed.kind !== "media") continue;
    const id: string = block.embed.media.attachmentId;
    if (embedded.includes(id)) {
      invalid(`${path}.blocks`, `attachment ${id} is embedded more than once`);
    }
    embedded.push(id);
  }

  const owned = attachments.map((attachment) => attachment.id as string);
  for (const id of embedded) {
    if (!owned.includes(id)) invalid(`${path}.blocks`, `embeds attachment ${id}, which is not attached`);
  }
  for (const id of owned) {
    if (!embedded.includes(id)) {
      // The two sets must be *equal*, not merely overlapping: an attachment
      // with no embed is media the reader will never show and the sweep will
      // never collect, because the entry still claims it.
      invalid(`${entryPath}.attachments`, `attachment ${id} has no media embed`);
    }
  }
}

/* ------------------------------------------------------------------------- *
 * Draft and goal
 * ------------------------------------------------------------------------- */

export function decodeJournalDraft(
  raw: unknown,
  mode: JournalValidationMode = "decode",
): JournalValidation<JournalDraft> {
  return run(() => {
    const source = object(raw, "$", ["schemaVersion", "id", "entry", "savedAt"]);
    schemaVersionAtMost(source["schemaVersion"], "$.schemaVersion", JOURNAL_DRAFT_SCHEMA_VERSION);
    const id = trimmedText(source["id"], "$.id");
    const entry = buildEntry(source["entry"], "$.entry", mode);
    if (entry.id !== id) {
      // A draft and its entry collide by construction — that is what makes
      // promotion a transaction rather than an id reconciliation.
      invalid("$.id", "must equal the contained entry's id");
    }
    return {
      schemaVersion: JOURNAL_DRAFT_SCHEMA_VERSION,
      id: id as JournalDraft["id"],
      entry,
      savedAt: instant(source["savedAt"], "$.savedAt") as JournalDraft["savedAt"],
    };
  });
}

const GOAL_STATUSES = ["active", "paused", "completed", "archived"] as const;

export function decodeJournalGoal(raw: unknown): JournalValidation<JournalGoal> {
  return run(() => {
    const source = object(raw, "$", [
      "schemaVersion",
      "id",
      "revision",
      "title",
      "why",
      "status",
      "targetDate",
      "createdAt",
      "updatedAt",
    ]);
    schemaVersionAtMost(source["schemaVersion"], "$.schemaVersion", JOURNAL_GOAL_SCHEMA_VERSION);
    const revision = integer(source["revision"], "$.revision");
    if (revision < 1) invalid("$.revision", "must be at least 1");
    if (revision > JOURNAL_REVISION_MAX) invalid("$.revision", "beyond the revision ceiling");

    const createdAt = instant(source["createdAt"], "$.createdAt");
    const updatedAt = instant(source["updatedAt"], "$.updatedAt");
    if (updatedAt < createdAt) invalid("$.updatedAt", "must not be earlier than createdAt");

    const why = optionalTrimmedText(source["why"], "$.why");
    const targetDate =
      source["targetDate"] === undefined
        ? undefined
        : instant(source["targetDate"], "$.targetDate");

    return {
      schemaVersion: JOURNAL_GOAL_SCHEMA_VERSION,
      id: trimmedText(source["id"], "$.id") as JournalGoal["id"],
      revision: revision as JournalGoal["revision"],
      title: trimmedText(source["title"], "$.title"),
      status: member(source["status"], "$.status", GOAL_STATUSES),
      createdAt: createdAt as JournalGoal["createdAt"],
      updatedAt: updatedAt as JournalGoal["updatedAt"],
      ...optionalField("why", why),
      ...optionalField("targetDate", targetDate as JournalGoal["targetDate"] | undefined),
    };
  });
}
